import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useHasPermission } from '@/auth/auth-store';
import {
  KIND_LABEL,
  KIND_LABEL_KEY,
  SIGNER_ROLE_LABEL,
  SIGNER_ROLE_LABEL_KEY,
  TAXATION_LABEL,
  TAXATION_LABEL_KEY,
  useLegalEntities,
  useSaveLegalEntity,
  type LegalEntity,
  type LegalEntityBody,
  type LegalEntityKind,
  type SignerRole,
  type TaxationSystem,
} from '@/hooks/use-fiscal-registration';
import { useT } from '@/i18n/use-t';
import { EmptyPanel, FiscalCabinetSection } from './fiscal-cabinet-section';
import {
  AddressFields,
  Banner,
  Field,
  SelectInput,
  TextInput,
  addressBody,
  addressDraft,
  serverError,
  type AddressDraft,
} from './fiscal-form-fields';
import {
  isTaxOfficeCode,
  textOrNull,
  validateLegalEntity,
  type FieldErrors,
} from './fiscal-validation';
import { TaxOfficePicker } from './fiscal-reference-pickers';

type Draft = {
  kind: LegalEntityKind;
  tin: string;
  name: string;
  taxationSystem: TaxationSystem;
  singleTaxGroup: string;
  vatNumber: string;
  taxOfficeCode: string;
  taxOfficeName: string;
  signerRole: SignerRole;
  signerName: string;
  signerTin: string;
  address: AddressDraft;
  phone: string;
  email: string;
  active: boolean;
};

function draftOf(entity: LegalEntity | null): Draft {
  return {
    kind: entity?.kind ?? 'fop',
    tin: entity?.tin ?? '',
    name: entity?.name ?? '',
    taxationSystem: entity?.taxationSystem ?? 'simplified',
    singleTaxGroup:
      entity?.singleTaxGroup === null || entity === null ? '' : String(entity.singleTaxGroup),
    vatNumber: entity?.vatNumber ?? '',
    taxOfficeCode: entity?.taxOfficeCode ?? '',
    taxOfficeName: entity?.taxOfficeName ?? '',
    signerRole: entity?.signerRole ?? 'head',
    signerName: entity?.signerName ?? '',
    signerTin: entity?.signerTin ?? '',
    address: addressDraft(entity?.address ?? null),
    phone: entity?.phone ?? '',
    email: entity?.email ?? '',
    active: entity?.active ?? true,
  };
}

function bodyOf(draft: Draft): LegalEntityBody {
  const group = draft.singleTaxGroup.trim();
  return {
    kind: draft.kind,
    tin: draft.tin.trim(),
    name: draft.name.trim(),
    taxationSystem: draft.taxationSystem,
    singleTaxGroup: group === '' ? null : Number(group),
    vatNumber: textOrNull(draft.vatNumber),
    taxOfficeCode: draft.taxOfficeCode.trim(),
    taxOfficeName: draft.taxOfficeName.trim(),
    signerRole: draft.signerRole,
    signerName: draft.signerName.trim(),
    signerTin: draft.signerTin.trim(),
    address: addressBody(draft.address),
    phone: textOrNull(draft.phone),
    email: textOrNull(draft.email),
    active: draft.active,
  };
}

function LegalEntityEditor({ entity, onDone }: { entity: LegalEntity | null; onDone: () => void }) {
  const t = useT('admin_ui');
  const save = useSaveLegalEntity();
  const [draft, setDraft] = useState<Draft>(() => draftOf(entity));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  async function onSave() {
    const body = bodyOf(draft);
    const found = validateLegalEntity(body);
    setErrors(found);
    setBanner(null);
    if (Object.keys(found).length > 0) return;
    try {
      await save.mutateAsync({ id: entity?.id ?? null, body });
      onDone();
    } catch (err) {
      const refused = serverError(err, t);
      setErrors(refused.fields);
      setBanner(refused.banner);
    }
  }

  const office = draft.taxOfficeCode.trim();
  // C_REG and C_RAJ, which the server stores derived from the code.
  const officeParts = isTaxOfficeCode(office)
    ? t('fiscalCabinet.legalEntity.officeParts', 'Region {region} · district {district}')
        .replace('{region}', office.slice(0, 2))
        .replace('{district}', office.slice(2, 4))
    : null;

  return (
    <section className="space-y-5 rounded-md border border-border bg-card p-5">
      <h2 className="text-base font-medium">
        {entity === null
          ? t('fiscalCabinet.legalEntity.new', 'New legal entity')
          : t('fiscalCabinet.legalEntity.edit', 'Edit legal entity')}
      </h2>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field
          id="le-kind"
          label={t('fiscalCabinet.legalEntity.field.kind', 'Kind')}
          errors={errors}
          name="kind"
        >
          <SelectInput
            id="le-kind"
            value={draft.kind}
            onChange={(v) => set('kind', v as LegalEntityKind)}
          >
            {(['fop', 'llc'] as const).map((kind) => (
              <option key={kind} value={kind}>
                {t(KIND_LABEL_KEY[kind], KIND_LABEL[kind])}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field
          id="le-tin"
          label={t('fiscalCabinet.legalEntity.field.tin', 'Tax ID (EDRPOU / RNOKPP)')}
          errors={errors}
          name="tin"
        >
          <TextInput
            id="le-tin"
            value={draft.tin}
            onChange={(v) => set('tin', v)}
            invalid={errors['tin'] !== undefined}
            mono
            maxLength={10}
          />
        </Field>
        <Field
          id="le-name"
          label={t('fiscalCabinet.legalEntity.field.name', 'Name')}
          errors={errors}
          name="name"
          className="col-span-2"
        >
          <TextInput
            id="le-name"
            value={draft.name}
            onChange={(v) => set('name', v)}
            invalid={errors['name'] !== undefined}
          />
        </Field>

        <Field
          id="le-taxation"
          label={t('fiscalCabinet.legalEntity.field.taxationSystem', 'Taxation system')}
          errors={errors}
          name="taxationSystem"
        >
          <SelectInput
            id="le-taxation"
            value={draft.taxationSystem}
            onChange={(v) =>
              // A single-tax group exists only on the simplified system.
              setDraft((current) => ({
                ...current,
                taxationSystem: v as TaxationSystem,
                singleTaxGroup: v === 'simplified' ? current.singleTaxGroup : '',
              }))
            }
          >
            {(['simplified', 'general'] as const).map((system) => (
              <option key={system} value={system}>
                {t(TAXATION_LABEL_KEY[system], TAXATION_LABEL[system])}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field
          id="le-group"
          label={t('fiscalCabinet.legalEntity.field.singleTaxGroup', 'Single-tax group')}
          errors={errors}
          name="singleTaxGroup"
        >
          <SelectInput
            id="le-group"
            value={draft.singleTaxGroup}
            onChange={(v) => set('singleTaxGroup', v)}
            disabled={draft.taxationSystem !== 'simplified'}
          >
            <option value="">—</option>
            {['1', '2', '3', '4'].map((group) => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field
          id="le-vat"
          label={t('fiscalCabinet.legalEntity.field.vatNumber', 'VAT number')}
          errors={errors}
          name="vatNumber"
          className="col-span-2"
        >
          <TextInput
            id="le-vat"
            value={draft.vatNumber}
            onChange={(v) => set('vatNumber', v)}
            invalid={errors['vatNumber'] !== undefined}
            mono
            maxLength={12}
          />
        </Field>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">
          {t('fiscalCabinet.legalEntity.section.taxOffice', 'Tax office')}
        </h3>
        <TaxOfficePicker
          code={draft.taxOfficeCode}
          onPick={(office) =>
            setDraft((current) => ({
              ...current,
              taxOfficeCode: office.code,
              taxOfficeName: office.name,
            }))
          }
        />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field
            id="le-office-code"
            label={t('fiscalCabinet.legalEntity.field.taxOfficeCode', 'Tax office code')}
            errors={errors}
            name="taxOfficeCode"
          >
            <TextInput
              id="le-office-code"
              value={draft.taxOfficeCode}
              onChange={(v) => set('taxOfficeCode', v)}
              invalid={errors['taxOfficeCode'] !== undefined}
              mono
              maxLength={4}
              placeholder="2650"
            />
            {officeParts !== null && (
              <p className="mt-1 font-mono text-xs text-muted-foreground">{officeParts}</p>
            )}
          </Field>
          <Field
            id="le-office-name"
            label={t('fiscalCabinet.legalEntity.field.taxOfficeName', 'Tax office name')}
            errors={errors}
            name="taxOfficeName"
            className="col-span-2 md:col-span-3"
          >
            <TextInput
              id="le-office-name"
              value={draft.taxOfficeName}
              onChange={(v) => set('taxOfficeName', v)}
              invalid={errors['taxOfficeName'] !== undefined}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">
          {t('fiscalCabinet.legalEntity.section.signer', 'Signer')}
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field
            id="le-signer-role"
            label={t('fiscalCabinet.legalEntity.field.signerRole', 'Signs as')}
            errors={errors}
            name="signerRole"
          >
            <SelectInput
              id="le-signer-role"
              value={draft.signerRole}
              onChange={(v) => set('signerRole', v as SignerRole)}
            >
              {(['head', 'representative'] as const).map((role) => (
                <option key={role} value={role}>
                  {t(SIGNER_ROLE_LABEL_KEY[role], SIGNER_ROLE_LABEL[role])}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field
            id="le-signer-name"
            label={t('fiscalCabinet.legalEntity.field.signerName', 'Full name')}
            errors={errors}
            name="signerName"
            className="col-span-2"
          >
            <TextInput
              id="le-signer-name"
              value={draft.signerName}
              onChange={(v) => set('signerName', v)}
              invalid={errors['signerName'] !== undefined}
            />
          </Field>
          <Field
            id="le-signer-tin"
            label={t('fiscalCabinet.legalEntity.field.signerTin', 'Tax ID')}
            errors={errors}
            name="signerTin"
          >
            <TextInput
              id="le-signer-tin"
              value={draft.signerTin}
              onChange={(v) => set('signerTin', v)}
              invalid={errors['signerTin'] !== undefined}
              mono
              maxLength={10}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">
          {t('fiscalCabinet.legalEntity.section.address', 'Legal address')}
        </h3>
        <AddressFields
          idPrefix="le"
          value={draft.address}
          onChange={(next) => set('address', next)}
          errors={errors}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field
          id="le-phone"
          label={t('fiscalCabinet.legalEntity.field.phone', 'Phone')}
          errors={errors}
          name="phone"
        >
          <TextInput
            id="le-phone"
            value={draft.phone}
            onChange={(v) => set('phone', v)}
            invalid={errors['phone'] !== undefined}
          />
        </Field>
        <Field
          id="le-email"
          label={t('fiscalCabinet.legalEntity.field.email', 'E-mail')}
          errors={errors}
          name="email"
        >
          <TextInput
            id="le-email"
            value={draft.email}
            onChange={(v) => set('email', v)}
            invalid={errors['email'] !== undefined}
          />
        </Field>
        <label className="col-span-2 flex items-center gap-2 self-end pb-2 text-sm">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => set('active', e.target.checked)}
          />
          {t(
            'fiscalCabinet.legalEntity.field.active',
            'Active — can be chosen for stores and cashiers',
          )}
        </label>
      </div>

      {banner !== null && <Banner>{banner}</Banner>}

      <div className="flex gap-2">
        <Button onClick={onSave} disabled={save.isPending}>
          {save.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
        </Button>
        <Button variant="outline" onClick={onDone}>
          {t('common.cancel', 'Cancel')}
        </Button>
      </div>
    </section>
  );
}

function LegalEntitiesList() {
  const t = useT('admin_ui');
  const canWrite = useHasPermission('fiscal.cabinet.write');
  const { data, isLoading, isError } = useLegalEntities();
  // null: list; 'new': create form; an entity: its edit form.
  const [editing, setEditing] = useState<LegalEntity | 'new' | null>(null);

  if (editing !== null) {
    return (
      <LegalEntityEditor
        entity={editing === 'new' ? null : editing}
        onDone={() => setEditing(null)}
      />
    );
  }
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={() => setEditing('new')}>
            {t('fiscalCabinet.legalEntity.add', 'Add legal entity')}
          </Button>
        </div>
      )}
      {data.items.length === 0 ? (
        <EmptyPanel>
          {t(
            'fiscalCabinet.legalEntities.empty',
            'The legal entities that file through this cabinet will be listed here.',
          )}
        </EmptyPanel>
      ) : (
        <div className="rounded-md border border-border bg-card">
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscalCabinet.legalEntity.field.name', 'Name')}</TH>
                <TH>{t('fiscalCabinet.legalEntity.col.tin', 'Tax ID')}</TH>
                <TH>{t('fiscalCabinet.legalEntity.field.kind', 'Kind')}</TH>
                <TH>{t('fiscalCabinet.legalEntity.field.taxationSystem', 'Taxation system')}</TH>
                <TH>{t('fiscalCabinet.legalEntity.section.taxOffice', 'Tax office')}</TH>
                <TH>{t('fiscalCabinet.legalEntity.col.status', 'Status')}</TH>
                {canWrite && <TH />}
              </TR>
            </THead>
            <TBody>
              {data.items.map((entity) => (
                <TR key={entity.id}>
                  <TD>{entity.name}</TD>
                  <TD className="font-mono text-xs">{entity.tin}</TD>
                  <TD>{t(KIND_LABEL_KEY[entity.kind], KIND_LABEL[entity.kind])}</TD>
                  <TD>
                    {t(
                      TAXATION_LABEL_KEY[entity.taxationSystem],
                      TAXATION_LABEL[entity.taxationSystem],
                    )}
                    {entity.singleTaxGroup !== null && ` · ${entity.singleTaxGroup}`}
                  </TD>
                  <TD className="text-xs">
                    <span className="font-mono">{entity.taxOfficeCode}</span> {entity.taxOfficeName}
                    {(entity.referenceWarnings ?? []).some((w) => w.field === 'taxOfficeCode') && (
                      <span className="ml-1 text-amber-700 dark:text-amber-400">
                        {t(
                          'fiscalCabinet.reference.notInDirectory',
                          'Not in the current directory',
                        )}
                      </span>
                    )}
                  </TD>
                  <TD>
                    <Badge variant={entity.active ? 'success' : 'outline'}>
                      {entity.active
                        ? t('fiscalCabinet.legalEntity.active', 'Active')
                        : t('fiscalCabinet.legalEntity.inactive', 'Inactive')}
                    </Badge>
                  </TD>
                  {canWrite && (
                    <TD className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setEditing(entity)}>
                        {t('common.edit', 'Edit')}
                      </Button>
                    </TD>
                  )}
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/** PFC-10 — Fiscal cabinet → Legal entities: the taxpayers that file through the cabinet. */
export function FiscalLegalEntitiesPage() {
  return <FiscalCabinetSection>{() => <LegalEntitiesList />}</FiscalCabinetSection>;
}
